import { API, graphqlOperation } from 'aws-amplify'
import { put, call, select } from 'redux-saga/effects'
import { CreateSupplier, UpdateSupplier } from '@wellbeingxltd/fe-lib/api/graphql'
import { GET_STORE } from '@wellbeingxltd/fe-lib/constants'
import { submitToApi } from 'awsApi'
import { SUPPLIERS_STATUS } from 'modules/Landing/Onboarding'
import SupplierActions from 'store/models/supplier'
import { URL } from './endpoints'

export function* getSupplier() {
    const cognitoUser = JSON.parse(localStorage.getItem('cognitoUser'))

    try {
        const { payload } = yield call(() => submitToApi({ url: URL.SUPPLIERS.BASE, path: URL.SUPPLIERS.V3_SUPPLIERS_DETAILS }))
        localStorage.setItem('supplier', JSON.stringify(payload.data))
        yield put(SupplierActions.getSupplierSuccess(payload.data))
    } catch (error) {
        if (cognitoUser?.attributes?.sub) {
            // Case to create new supplier
            return yield call(createSupplier, { email: cognitoUser.attributes.email, supplierId: cognitoUser.attributes.sub })
        }

        yield put(SupplierActions.getSupplierFailure(error))
        alert(
            `Oops, something went wrong on our end. Please send a screenshot of this error to support@betterspace.uk\n\n. The following error was caught in getSupplier:\n\n${JSON.stringify(
                error
            )}`
        )
    }
}

export function* createSupplier({ email, supplierId }) {
    try {
        const response = yield call(() =>
            API.graphql(
                graphqlOperation(CreateSupplier, {
                    input: {
                        primaryContactEmail: email,
                        supplierId,
                    },
                })
            )
        )
        yield put(SupplierActions.createSupplierSuccess(response.data.createSupplier))

        //check if supplier has company name
        localStorage.setItem('supplier', JSON.stringify(response.data.createSupplier))
        yield call(getSupplier)
    } catch (error) {
        console.log('create supplier error: ', error)
        yield put(SupplierActions.createSupplierFailure())
    }
}

export function* updateSupplier({ body, callback }) {
    try {
        const {
            data: { updateSupplierDetails },
        } = yield call(() => API.graphql(graphqlOperation(UpdateSupplier, { input: body })))

        localStorage.setItem('supplier', JSON.stringify(updateSupplierDetails))

        // Case for onboarding. Redirect to Stripe
        if (updateSupplierDetails.status === SUPPLIERS_STATUS.INCOMPLETE) {
            yield call(reauthenticateSupplier, { supplierId: updateSupplierDetails.supplierId })
            return
        }

        yield put(SupplierActions.updateSupplierSuccess(updateSupplierDetails))
        yield call(getSupplier)

        callback && callback('Successfully updated supplier', 'success')
    } catch (error) {
        const message = JSON.stringify(error)
        yield put(SupplierActions.updateSupplierFailure(message))
        callback && callback('Failed to update supplier', message)
    }
}

export function* reauthenticateSupplier({ supplierId }) {
    try {
        yield put(SupplierActions.reauthenticateSupplierLoading())

        const { payload } = yield call(() =>
            submitToApi({
                body: { supplierId },
                method: 'POST',
                path: URL.SUPPLIERS.V3_SUPPLIERS_SUPPLIER_REGENERATE_SUPPLIER_ACCOUNT_ID,
                url: URL.SUPPLIERS.BASE,
            })
        )
        window.location.replace(payload.data) // Redirect to Stripe url
        yield put(SupplierActions.reauthenticateSupplierSuccess())
    } catch (error) {
        console.log('reauthenticate supplier error: ', error)
        yield put(SupplierActions.reauthenticateSupplierFailure(error))
        alert('Something went wrong with supplier reauth, please contact supplier.management@betterspace.uk and quote this error message.')
    }
}

export function* resetProfileForTests({ callback }) {
    try {
        const { transactions } = yield select(GET_STORE.TRANSACTIONS)
        const { allTickets } = yield select(GET_STORE.TICKETS)

        const body = transactions.length
            ? {
                  transactions: transactions.map(({ transactionId, userId }) => ({ transactionId, userId })),
                  tickets: allTickets.map(({ ticketId }) => ticketId),
              }
            : {}

        const response = yield call(() =>
            submitToApi({ body, method: 'POST', path: URL.SUPPLIERS.V3_SUPPLIERS_SUPPLIER_RESET_ATTRIBUTES, url: URL.SUPPLIERS.BASE })
        )
        console.log('resetProfileForTests response: ', response)
    } catch (error) {
        console.log('resetProfileForTests error: ', error)
    } finally {
        callback()
    }
}

export function* getShopifyProducts({ supplierId }) {
    try {
        const { payload } = yield call(() =>
            submitToApi({ path: URL.SUPPLIERS.V3_SUPPLIERS_SHOPIFY_PRODUCTS_RETRIEVE_ALL, query: { supplierId }, url: URL.SUPPLIERS.BASE })
        )
        yield put(SupplierActions.getShopifyProductsSuccess(payload.data))
    } catch (error) {
        yield put(SupplierActions.getShopifyProductsFailure(error))
    }
}

export function* getShopifyProductsCount({ supplierId }) {
    try {
        const { message, payload } = yield call(() =>
            submitToApi({
                path: URL.SUPPLIERS.V3_SUPPLIERS_SHOPIFY_PRODUCTS_RETRIEVE_ALL,
                query: { supplierId, count: true },
                url: URL.SUPPLIERS.BASE,
            })
        )
        if (message) {
            return yield put(SupplierActions.getShopifyProductsCountFailure(message))
        }
        yield put(SupplierActions.getShopifyProductsCountSuccess(payload.data))
    } catch (error) {
        yield put(SupplierActions.getShopifyProductsCountFailure(error))
    }
}

export function* getShopifyDetails({ supplierId }) {
    try {
        const { payload } = yield call(() =>
            submitToApi({ path: URL.SUPPLIERS.V3_SUPPLIERS_SHOPIFY_ACCOUNT, query: { supplierId }, url: URL.SUPPLIERS.BASE })
        )
        yield put(SupplierActions.getShopifyDetailsSuccess(payload.data))
    } catch (error) {
        yield put(SupplierActions.getShopifyDetailsFailure(error))
    }
}

export function* updateShopifyDetails({ body, callback }) {
    try {
        const { message } = yield call(() =>
            submitToApi({ body, method: 'PUT', path: URL.SUPPLIERS.V3_SUPPLIERS_SHOPIFY_ACCOUNT_INTEGRATE, url: URL.SUPPLIERS.BASE })
        )
        yield put(SupplierActions.updateShopifyDetailsSuccess())
        callback(message, 'success')
    } catch (error) {
        yield put(SupplierActions.updateShopifyDetailsFailure(JSON.stringify(error)))
        callback(error, 'error')
    }
}

// Remove Supplier's Shopify details from the platform (All the connected resources and Shopify credentials)
export function* removeShopifyDetails({ supplierId, callback }) {
    try {
        const { message } = yield call(() =>
            submitToApi({ method: 'PUT', path: URL.SUPPLIERS.V3_SUPPLIERS_SHOPIFY_ACCOUNT_REMOVE, query: { supplierId }, url: URL.SUPPLIERS.BASE })
        )
        yield put(SupplierActions.removeShopifyDetailsSuccess())
        callback(message, 'success')
    } catch (error) {
        yield put(SupplierActions.removeShopifyDetailsFailure())
        callback(error, 'error')
    }
}
